import { RestClient, RequestResponse } from "./base/rest.client";
import { CommonDto } from "../common.dto";
import { BASE_URL } from "../config";
import { OrderHistoryListItem, OrderHistoryItem } from "@domain/dtos/models/order.models";
import * as z from "zod";

export interface CreateOrderRequest {
  machine_id: number;
  /** Гасить ли заказ бесплатной печатью */
  use_free_prints?: boolean;
  /** Гасить ли заказ баллами */
  use_points?: boolean;
  services: Array<{
    service_type: string;
    files: Array<{
      file_id: string;
      copies: number;
      pages: string[] | "all";
      parameters: {
        color: string | null;
        duplex: boolean;
        format: string;
      };
      total_pages: number;
    }>;
  }>;
}

export interface CreateOrderResponse {
  status: string;
}

export interface CalculateOrderRequest {
  machine_id: number;
  /** Гасить ли заказ бесплатной печатью. По умолчанию да — награда заработана. */
  use_free_prints?: boolean;
  /** Гасить ли заказ баллами */
  use_points?: boolean;
  services: Array<{
    service_type: string;
    files: Array<{
      file_id: string;
      copies: number;
      pages: string[] | "all";
      parameters: {
        color: string | null;
        duplex: boolean;
        format: string;
      };
      total_pages: number;
    }>;
  }>;
}

export interface PromoCodeInfo {
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
}

export interface CalculateOrderResponse {
  total_price: number;
  original_price: number;
  discount: number;
  files: Array<{
    filename: string;
    price: number;
  }>;
  promo_code: PromoCodeInfo | null;
  /** Сколько бесплатных отпечатков ушло в этот заказ, по услугам */
  free_prints_used: Record<string, number>;
  /** Сколько бесплатных отпечатков есть всего */
  free_prints_available: Record<string, number>;
  /** На сколько бесплатная печать уменьшила цену */
  free_prints_discount: number;
  /** Сколько баллов списывается */
  points_used: number;
  /** Сколько баллов есть всего */
  points_available: number;
  /** Сколько нужно оплатить деньгами в любом случае */
  min_card_payment: number;
}

const CreateOrderResponseSchema = z.object({
  status: z.string()
});

const PromoCodeInfoSchema = z.object({
  code: z.string(),
  description: z.string(),
  discount_type: z.string(),
  discount_value: z.number(),
});

const CalculateOrderResponseSchema = z.object({
  total_price: z.number(),
  original_price: z.number(),
  discount: z.number(),
  files: z.array(z.object({
    filename: z.string(),
    price: z.number(),
  })),
  promo_code: PromoCodeInfoSchema.nullable().optional(),
  // Награды приходят с сервера. Значения по умолчанию нужны, чтобы старый ответ
  // не ломал экран во время выката: сначала фронт, потом бэкенд — или наоборот.
  free_prints_used: z.record(z.string(), z.number()).default({}),
  free_prints_available: z.record(z.string(), z.number()).default({}),
  free_prints_discount: z.number().default(0),
  points_used: z.number().default(0),
  points_available: z.number().default(0),
  min_card_payment: z.number().default(1),
});

const OrderHistoryListItemSchema = z.array(z.any()); // Упрощенная схема, можно доработать позже
const OrderHistoryItemSchema = z.any(); // Упрощенная схема, можно доработать позже

export interface OpenCellResponse {
  machine_id: number;
  cell_id: number;
  issued_orders: string[];
}

const OpenCellResponseSchema = z.object({
  machine_id: z.number(),
  cell_id: z.number(),
  issued_orders: z.array(z.string()),
});

const OrderStatusConfigItemSchema = z.object({
  status: z.string(),
  label: z.string(),
  icon: z.string(),
});

const OrderStatusConfigResponseSchema = z.object({
  statuses: z.array(OrderStatusConfigItemSchema),
});

export interface OrderStatusConfigItem {
  status: string;
  label: string;
  icon: string;
}

class OrderClientClass extends RestClient {
  baseUrl = BASE_URL;

  /**
   * Получает историю заказов пользователя
   */
  async getOrderHistory(): Promise<RequestResponse<OrderHistoryListItem[]>> {
    const result = await this.request("GET", "/api/orders/history");
    return this.validateResponse(result, CommonDto.withStatus(OrderHistoryListItemSchema));
  }

  /**
   * Получает заказ по номеру
   */
  async getOrderById(orderNumber: string): Promise<RequestResponse<OrderHistoryItem>> {
    const result = await this.request("GET", `/api/orders/${orderNumber}`);
    return this.validateResponse(result, CommonDto.withStatus(OrderHistoryItemSchema));
  }

  /**
   * Рассчитывает финальную стоимость заказа перед созданием
   * Используется для получения total_price, discount и available перед оплатой
   */
  async calculateOrder(data: CalculateOrderRequest): Promise<RequestResponse<CalculateOrderResponse>> {
    const result = await this.request("POST", "/api/orders/calculate", {
      jsonPayload: data
    });
    return this.validateResponse(result, CommonDto.withStatus(CalculateOrderResponseSchema));
  }

  /**
   * Создает новый заказ
   */
  async createOrder(data: CreateOrderRequest): Promise<RequestResponse<CreateOrderResponse>> {
    const result = await this.request("POST", "/api/orders/create/mobile", {
      jsonPayload: data
    });
    return this.validateResponse(result, CommonDto.withStatus(CreateOrderResponseSchema));
  }

  /**
   * Открывает ячейку с готовым заказом.
   * Пользователь должен находиться возле аппарата.
   */
  async openCell(orderNumber: string): Promise<RequestResponse<OpenCellResponse>> {
    const result = await this.request("POST", `/api/orders/${orderNumber}/open-cell`);
    return this.validateResponse(result, CommonDto.withStatus(OpenCellResponseSchema));
  }

  /**
   * Получает конфиг статусов заказа (label, icon для UI)
   */
  async getOrderStatusConfig(): Promise<RequestResponse<OrderStatusConfigItem[]>> {
    const result = await this.request("GET", "/api/orders/statuses");
    const validated = this.validateResponse(result, CommonDto.withStatus(OrderStatusConfigResponseSchema));
    return { status: validated.status, data: validated.data.statuses, details: validated.details };
  }
}

export class OrderClient extends OrderClientClass {}
